<template>
  <div class="kyc-answers">
    <DataSection title="Kontaktuppgifter">
      <DataField :label="label('mobilePhone')">{{ answers.mobilePhone ?? '–' }}</DataField>
      <DataField :label="label('email')">{{ answers.email ?? '–' }}</DataField>
    </DataSection>

    <DataSection title="Medborgarskap och skatterättslig hemvist">
      <DataField :label="label('citizenships')">
        <template v-if="answers.citizenships">
          <span v-for="(entry, index) in answers.citizenships" :key="entry.countryCode" class="country-line">
            {{ borgoCountryName(entry.countryCode) }}: {{ entry.registrationId }}{{ index === 0 ? ' (primärt)' : '' }}
          </span>
        </template>
        <template v-else>–</template>
      </DataField>
      <DataField :label="label('taxDomiciles')">
        <template v-if="answers.taxDomiciles">
          <span v-for="entry in answers.taxDomiciles" :key="entry.countryCode" class="country-line">
            {{ borgoCountryName(entry.countryCode) }}: {{ entry.tin }}{{ entry.isPrimary ? ' (primärt)' : '' }}
          </span>
        </template>
        <template v-else>–</template>
      </DataField>
    </DataSection>

    <DataSection title="Anställning och inkomst">
      <DataField :label="label('employmentDetails.employmentStatus')">
        {{ employment ? translateRef('kycEmploymentStatuses', employment.employmentStatus) : '–' }}
      </DataField>
      <DataField v-if="employment" :label="label('employmentDetails.employmentStartYearMonth')">
        {{ employment.employmentStartYearMonth }}
      </DataField>
      <DataField v-if="employment?.employmentEndYearMonth" :label="label('employmentDetails.employmentEndYearMonth')">
        {{ employment.employmentEndYearMonth }}
      </DataField>
      <DataField v-if="!employment || hasEmployer(employment.employmentStatus)" :label="label('employer')">
        {{ answers.employer ?? '–' }}
      </DataField>
      <DataField :label="label('monthlyIncome')">
        {{ answers.monthlyIncome !== undefined ? `${formatAmount(answers.monthlyIncome)} kr/mån` : '–' }}
      </DataField>
    </DataSection>

    <DataSection title="Extra amorteringar">
      <DataField :label="label('extraAmortizationFrequency')">
        {{
          answers.extraAmortizationFrequency
            ? translateRef('kycExtraAmortizationFrequencies', answers.extraAmortizationFrequency)
            : '–'
        }}
      </DataField>
      <DataField :label="label('extraAmortizationVolume')">
        {{
          answers.extraAmortizationVolume
            ? translateRef('kycExtraAmortizationVolumes', answers.extraAmortizationVolume)
            : '–'
        }}
      </DataField>
    </DataSection>

    <DataSection title="PEP">
      <DataField :label="label('pepStatus')">
        {{ answers.pepStatus ? translateRef('kycPepStatuses', answers.pepStatus) : '–' }}
      </DataField>
    </DataSection>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { borgoCountryName, formatAmount } from '@staff/shared'

import DataField from '@/components/DataField.vue'
import DataSection from '@/components/DataSection.vue'
import { useMetadata } from '@/composables/useMetadata'
import type { KycAnswers } from '@/services/kyc'
import { hasEmployer } from './kycForm'

const props = defineProps<{
  /** Unanswered questions show a dash. */
  answers: Partial<KycAnswers>
}>()

const { fieldLabel, translateRef } = useMetadata()

const label = (field: string) => fieldLabel('kycAnswers', field)
const employment = computed(() => props.answers.employmentDetails)
</script>

<style scoped lang="scss">
.kyc-answers {
  display: flex;
  flex-direction: column;
  gap: var(--space-16);
}

.country-line {
  display: block;
}
</style>
